import { ref, type Ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import type { KevelPlacementRequest } from '../../shared/payload'
import type { KevelDecisionResponse, KevelPlacementResult } from '../../shared/decisionResponse'
import type { CategoryBreadcrumb } from '../../shared/keywords'

/**
 * Batches placements whose observers fire in the same microtask into a single request.
 * Keyed by the microtask generation — flushed and replaced every time the queue empties.
 */
interface BatchState {
    placements: KevelPlacementRequest[]
    promise: Promise<KevelDecisionResponse> | null
    breadcrumb: CategoryBreadcrumb | undefined
}

let batchId = 0
let currentBatch: BatchState | null = null

function getBatch(breadcrumb?: CategoryBreadcrumb): BatchState {
    if (currentBatch == null) {
        currentBatch = {
            placements: [],
            promise: null,
            breadcrumb,
        }
        // Schedule flush at end of microtask
        queueMicrotask(() => {
            if (currentBatch != null && currentBatch.placements.length > 0 && currentBatch.promise == null) {
                flushBatch()
            }
        })
    }
    // A placement without breadcrumb may have opened the batch; keep the page's breadcrumb
    // from whichever placement has one, so the whole batch gets category keywords
    currentBatch.breadcrumb ??= breadcrumb
    return currentBatch
}

async function flushBatch(): Promise<KevelDecisionResponse> {
    if (currentBatch == null || currentBatch.placements.length === 0) {
        throw new Error('[useKevelDecision] Cannot flush empty batch')
    }

    const batch = currentBatch
    const thisId = ++batchId

    const promise = $fetch<KevelDecisionResponse>('/api/kevel/decision', {
        method: 'POST',
        body: {
            placements: batch.placements,
            breadcrumb: batch.breadcrumb,
        },
        timeout: 2000,
    }).catch((error) => {
        console.error(`[kevel] batch ${thisId}: decision request failed`, {
            divNames: batch.placements.map(p => p.divName),
            error: error instanceof Error ? { name: error.name, message: error.message } : error,
        })
        // Re-throw to propagate to individual callers
        throw error
    })

    batch.promise = promise
    currentBatch = null

    return await promise
}

export interface UseKevelDecisionInput {
    /** The element to observe for visibility. */
    target: Ref<HTMLElement | null>
    /** The placement configuration. */
    placement: KevelPlacementRequest
    /** Whether this placement is above the fold (skips intersection observer). */
    aboveTheFold?: boolean
    /** Optional category breadcrumb for keyword targeting. */
    breadcrumb?: CategoryBreadcrumb
}

export interface UseKevelDecisionOutput {
    /** The decision result for this placement (null before the request completes). */
    decision: Ref<KevelPlacementResult | null>
    /** Whether a request is in flight. */
    isLoading: Ref<boolean>
    /**
     * Whether the decision is final (filled, no-fill or error). Unlike `!isLoading`, this is
     * false before a below-the-fold request starts, so placeholders can be server-rendered.
     */
    isSettled: Ref<boolean>
    /** Any error that occurred during the request. */
    error: Ref<Error | null>
}

/**
 * Requests a Kevel ad decision for a single placement, batching with other placements
 * whose observers fire in the same microtask. Below-the-fold placements wait until
 * scrolling into view before issuing the request.
 */
export function useKevelDecision({
    target,
    placement,
    aboveTheFold = false,
    breadcrumb,
}: UseKevelDecisionInput): UseKevelDecisionOutput {
    const decision = ref<KevelPlacementResult | null>(null)
    const isLoading = ref(false)
    const isSettled = ref(false)
    const error = ref<Error | null>(null)
    const hasRequested = ref(false)

    async function requestDecision() {
        if (hasRequested.value) return
        hasRequested.value = true
        isLoading.value = true
        error.value = null

        try {
            // Check for early-fired decision (issue #124: above-the-fold optimization)
            // Non-blocking check: only use early fire if already completed, otherwise fall through
            if (aboveTheFold && import.meta.client && window.__KEVEL_EARLY_DECISIONS__) {
                const early = window.__KEVEL_EARLY_DECISIONS__
                const earlyDecision = early.decisions[placement.divName]
                if (earlyDecision != null) {
                    decision.value = earlyDecision
                    isLoading.value = false
                    return // Early fire already completed, skip normal request
                }
                // Early fire not ready yet, fall through to normal request
                // This ensures we don't block hydration waiting for slow early fire responses
            }

            const batch = getBatch(breadcrumb)
            batch.placements.push(placement)

            // Wait for the batch to flush
            let response: KevelDecisionResponse
            if (batch.promise != null) {
                // Already flushed by another caller in this batch
                response = await batch.promise
            } else {
                // This caller arrived first; it gets to flush
                response = await flushBatch()
            }

            // Extract this placement's decision from the batch response
            decision.value = response.decisions[placement.divName] ?? null
        } catch (err) {
            error.value = err instanceof Error ? err : new Error(String(err))
        } finally {
            isLoading.value = false
            isSettled.value = true
        }
    }

    if (aboveTheFold) {
        // Above the fold: request immediately
        requestDecision()
    } else {
        // Below the fold: wait until visible
        useIntersectionObserver(
            target,
            (entries) => {
                const entry = entries[0]
                if (entry?.isIntersecting && !hasRequested.value) {
                    requestDecision()
                }
            },
            {
                threshold: 0.01, // Fire as soon as even 1% is visible
            }
        )
    }

    return {
        decision,
        isLoading,
        isSettled,
        error,
    }
}
